import React from 'react';
import { Link } from 'react-router-dom';
import { Zap, Shield, Sparkles } from 'lucide-react';
import { useTheme } from '../theme';

const MORE_TOOLS = [
  { to: '/url-safe-base64', title: 'URL-Safe Base64', desc: 'For JWTs, tokens and URLs.' },
  { to: '/image-to-base64', title: 'Image to Base64', desc: 'Encode images as data URIs.' },
  { to: '/base64-to-image', title: 'Base64 to Image', desc: 'Decode and download images.' },
  { to: '/json-to-base64', title: 'JSON to Base64', desc: 'Encode JSON payloads.' },
  { to: '/hex-to-base64', title: 'Hex to Base64', desc: 'Convert between hex and Base64.' },
  { to: '/base64-encode-javascript', title: 'Code examples', desc: 'Base64 in JS, Python, Java, PHP.' },
];

export function MoreTools() {
  const { themeClasses } = useTheme();
  return (
    <section>
      <h2 className={`text-2xl font-bold mb-6 ${themeClasses.textPrimary}`}>More Base64 tools</h2>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {MORE_TOOLS.map((t) => (
          <Link
            key={t.to}
            to={t.to}
            className={`${themeClasses.infoCard} rounded-2xl p-5 border block transition-transform hover:scale-105`}
          >
            <h3 className={`font-semibold mb-1 ${themeClasses.textPrimary}`}>{t.title}</h3>
            <p className={`text-sm ${themeClasses.textSecondary}`}>{t.desc}</p>
          </Link>
        ))}
      </div>
    </section>
  );
}

export function FeaturesGrid() {
  const { themeClasses } = useTheme();
  const features = [
    { icon: Zap, title: 'Lightning Fast', text: 'Real-time conversion as you type, with results in milliseconds.' },
    { icon: Shield, title: '100% Private', text: 'All processing happens locally in your browser. Your data never leaves your device.' },
    { icon: Sparkles, title: 'Feature Rich', text: 'File upload, download, copy, URL-safe mode and smart error handling built in.' },
  ];

  return (
    <div className="grid md:grid-cols-3 gap-6">
      {features.map((f) => (
        <div key={f.title} className={`${themeClasses.featureCard} rounded-2xl p-6 border transition-colors`}>
          <div className="w-11 h-11 bg-indigo-600 rounded-xl flex items-center justify-center mb-4">
            <f.icon className="w-6 h-6 text-white" />
          </div>
          <h3 className={`text-xl font-semibold ${themeClasses.textPrimary} mb-2`}>{f.title}</h3>
          <p className={themeClasses.textSecondary}>{f.text}</p>
        </div>
      ))}
    </div>
  );
}

export function WhatIsBase64() {
  const { darkMode, themeClasses } = useTheme();
  return (
    <section className={`${themeClasses.infoCard} rounded-2xl p-5 sm:p-8 border`}>
      <h2 className={`text-2xl font-bold ${themeClasses.textPrimary} mb-6`}>What is Base64 encoding?</h2>
      <div className={`space-y-4 ${themeClasses.textSecondary} leading-relaxed`}>
        <p>
          Base64 is a binary-to-text encoding scheme that converts binary data into an ASCII text
          format using a radix-64 representation. It uses 64 characters (A–Z, a–z, 0–9, + and /) to
          represent data, making it safe to transmit over text-based protocols like email and HTTP.
        </p>
        <p>
          Base64 is an encoding, not encryption — it does not secure your data, it only makes binary
          data safe to move through systems that expect text.
        </p>
        <div className={`mt-6 p-4 rounded-xl border ${darkMode ? 'bg-indigo-500/10 border-indigo-500/20' : 'bg-indigo-50 border-indigo-100'}`}>
          <p className={`${themeClasses.accentText} font-medium`}>
            💡 Pro tip: Base64 increases data size by roughly 33%, but guarantees safe transmission
            across all systems.
          </p>
        </div>
      </div>
    </section>
  );
}

export function UseCases() {
  const { darkMode, themeClasses } = useTheme();
  const items = [
    { icon: '📧', text: 'Email attachments (MIME encoding)' },
    { icon: '🖼️', text: 'Embedding images in HTML/CSS (data URIs)' },
    { icon: '🔗', text: 'API and JSON data transmission' },
    { icon: '⚙️', text: 'Configuration files and secrets storage' },
    { icon: '💾', text: 'Storing binary data in databases' },
    { icon: '🌐', text: 'URL-safe data encoding (JWTs, tokens)' },
  ];
  return (
    <section className={`${themeClasses.infoCard} rounded-2xl p-5 sm:p-8 border`}>
      <h2 className={`text-2xl font-bold ${themeClasses.textPrimary} mb-6`}>Common use cases</h2>
      <div className="space-y-3">
        {items.map((item, i) => (
          <div key={i} className={`flex items-center gap-3 p-3 rounded-xl border ${darkMode ? 'bg-slate-950/40 border-slate-800' : 'bg-slate-50 border-slate-100'}`}>
            <span className="text-xl">{item.icon}</span>
            <span className={themeClasses.textSecondary}>{item.text}</span>
          </div>
        ))}
      </div>
    </section>
  );
}

// Shared page intro: a single keyword-rich H1 plus a supporting paragraph.
export function PageHero({ title, subtitle }: { title: React.ReactNode; subtitle: React.ReactNode }) {
  const { themeClasses } = useTheme();
  return (
    <div className="text-center mb-10">
      <h1 className={`text-4xl md:text-5xl font-bold tracking-tight ${themeClasses.textPrimary} mb-4`}>
        {title}
      </h1>
      <p className={`text-lg md:text-xl ${themeClasses.textSecondary} max-w-3xl mx-auto leading-relaxed`}>
        {subtitle}
      </p>
    </div>
  );
}
